import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentDevice } from "../context/CurrentDeviceContext";
import { Nav } from "rsuite";
import { Icon } from "@rsuite/icons";
import TerminalIcon from "@rsuite/icons/legacy/TerminalLine";
import LocationIcon from "@rsuite/icons/Location";
import DeviceIcon from "@rsuite/icons/Device";
import FileIcon from "@rsuite/icons/legacy/FileO";
import ReloadIcon from "@rsuite/icons/Reload";
import OffIcon from "@rsuite/icons/Off";
import FileUploadIcon from "@rsuite/icons/legacy/FileUpload";
import FileDownloadIcon from "@rsuite/icons/legacy/FileDownload";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import TrashIcon from "@rsuite/icons/Trash";
import UserInfoIcon from "@rsuite/icons/UserInfo";
import UserChangeIcon from "@rsuite/icons/UserChange";
import LockIcon from "@rsuite/icons/legacy/Lock";
import PowerOffIcon from "@rsuite/icons/legacy/PowerOff";
import GearIcon from "@rsuite/icons/Gear";
import { MdOutlineMonitorHeart, MdOutlineManageHistory } from "react-icons/md";
import { CiFloppyDisk } from "react-icons/ci";
import { GiGears } from "react-icons/gi";
import { VscDebugRestart, VscRemoteExplorer } from "react-icons/vsc";
import { BiScreenshot } from "react-icons/bi";
import { RxUpdate } from "react-icons/rx";
import { MdMyLocation } from "react-icons/md";
import { SlLocationPin } from "react-icons/sl";
import dev from "../models/joinDevice.json";
import "./DeviceMenu.css";

const DeviceInstalledSoftware = lazy(() => import("./DeviceInstalledSoftware"));
const DeviceSystemProcesses = lazy(() => import("./DeviceSystemProcesses"));
const LocationByDate = lazy(() => import("./LocationByDate"));
const LiveLocation = lazy(() => import("./LiveLocation"));
const FileUploadHandler = lazy(() => import("./FileUploadHandler"));
const RemoteShell = lazy(() => import("./RemoteShell"));
const RemoteShell2 = lazy(() => import("./RemoteShell2"));
const Terminal = lazy(() => import("./Terminal"));
const RemoveDevice = lazy(() => import("./RemoveDevice"));
const DeviceInstalledApplications = lazy(() =>
  import("./DeviceInstalledApplications")
);
const DeviceRemoteDesktop = lazy(() => import("./DeviceRemoteDesktop"));
const LinuxAppsSpotlight = lazy(() =>
  import("./linux-apps/LinuxAppsSpotlight")
);
const LinuxProcessesList = lazy(() =>
  import("./linux-processes/LinuxProcessesList")
);
const LinuxHealthMonitor = lazy(() =>
  import("./linux-health/LinuxHealthMonitor")
);

const DeviceMenu = ({ x, y, deleteDeviceSelected }) => {
  const [t] = useTranslation();
  const { currentDevice: deviceSelected } = useCurrentDevice();
  const [openModal, setOpenModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [openInstalledSoftware, setOpenInstalledSoftware] = useState(false);
  const [openSystemProcesses, setOpenSystemProcesses] = useState(false);
  const [openLocation, setOpenLocation] = useState(false);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [openLiveLocation, setOpenLiveLocation] = useState(false);
  const [openShell, setOpenShell] = useState(false);
  const [openShell2, setOpenShell2] = useState(false);
  const [openTerminal, setOpenTerminal] = useState(false);
  const [openInstalledApps, setOpenInstalledApps] = useState(false);
  const [openRemoteDesktop, setOpenRemoteDesktop] = useState(false);
  const [openLinuxApps, setOpenLinuxApps] = useState(false);
  const [openLinuxProcesses, setOpenLinuxProcesses] = useState(false);
  const [openLinuxHealth, setOpenLinuxHealth] = useState(false);
  const menuItemOpened = useRef(false);
  const menuClick = useRef(false);

  useEffect(() => {
    const handleClick = () => {
      if (!menuClick.current) setShowMenu(false);
      else {
        if (!menuItemOpened.current) menuClick.current = false;
      }
    };
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deviceSelected != null && deviceSelected.id) setShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSelected]);

  function handleOpenModal() {
    setOpenModal(true);
  }

  function removeDeviceHandler(isDelete) {
    if (isDelete) {
      deleteDeviceSelected();
    }
    setOpenModal(false);
    setShowMenu(false);
  }

  function onlyCloseMenu(isOpen) {
    if (isOpen) {
      menuItemOpened.current = true;
      menuClick.current = true;
    } else {
      menuItemOpened.current = false;
    }
    setShowMenu(false);
  }

  function deleteHandler() {
    setShowMenu(false);
    if (deviceSelected != null) {
      handleOpenModal();
    }
  }

  function installedSoftwareHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenInstalledSoftware(isOpen);
  }

  function systemProcessesHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenSystemProcesses(isOpen);
  }

  function locationHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenLocation(isOpen);
  }

  function fileUploadHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenFileUpload(isOpen);
  }

  function liveLocationHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenLiveLocation(isOpen);
  }

  function shellHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenShell(isOpen);
  }

  function shellHandler2(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenShell2(isOpen);
  }

  function terminalHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenTerminal(isOpen);
  }

  function installedAppsHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenInstalledApps(isOpen);
  }

  function remoteDesktopHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenRemoteDesktop(isOpen);
  }

  function linuxAppsHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenLinuxApps(isOpen);
  }

  function linuxProcessesHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenLinuxProcesses(isOpen);
  }

  function linuxHealthHandler(isOpen) {
    onlyCloseMenu(isOpen);
    setOpenLinuxHealth(isOpen);
  }

  return (
    <>
      {showMenu && deviceSelected != null && (
        <Nav
          vertical
          className="content content-final nav-menu"
          style={{ top: y, left: x }}
          id="device-menu"
          onClick={() => (menuClick.current = true)}
        >
          {deviceSelected.type !== dev.types.android && (
            <Nav.Item
              icon={<TerminalIcon />}
              onClick={() => shellHandler(true)}
            >
              {t("device-menu.remote-shell")}
            </Nav.Item>
          )}
          {deviceSelected.type !== dev.types.android && (
            <Nav.Item
              icon={<TerminalIcon />}
              onClick={() => shellHandler2(true)}
            >
              {t("device-menu.remote-shell") + "2"}
            </Nav.Item>
          )}
          {deviceSelected.type !== dev.types.android && (
            <Nav.Item
              icon={<TerminalIcon />}
              onClick={() => terminalHandler(true)}
            >
              {t("device-menu.terminal")}
            </Nav.Item>
          )}
          {deviceSelected.type !== dev.types.android && (
            <Nav.Item
              icon={<Icon as={VscRemoteExplorer} />}
              onClick={() => remoteDesktopHandler(true)}
            >
              {t("device-menu.remote-desktop")}
            </Nav.Item>
          )}
          {deviceSelected.type === dev.types.linux && (
            <Nav.Item
              icon={<Icon as={CiFloppyDisk} />}
              onClick={() => linuxAppsHandler(true)}
            >
              {t("device-menu.software-and-applications")}
            </Nav.Item>
          )}
          {deviceSelected.type === dev.types.linux && (
            <Nav.Item
              icon={<Icon as={GiGears} />}
              onClick={() => linuxProcessesHandler(true)}
            >
              {t("device-menu.processes-list")}
            </Nav.Item>
          )}
          {deviceSelected.type === dev.types.linux && (
            <Nav.Item
              icon={<Icon as={MdOutlineMonitorHeart} />}
              onClick={() => linuxHealthHandler(true)}
            >
              {t("device-menu.system-health")}
            </Nav.Item>
          )}
          {deviceSelected.type === dev.types.android && (
            <Nav.Menu icon={<Icon as={SlLocationPin} />} title={"GPS"}>
              <Nav.Item
                icon={<Icon as={MdMyLocation} />}
                onClick={() => liveLocationHandler(true)}
              >
                {t("device-menu.live-location")}
              </Nav.Item>
              <Nav.Item
                icon={<LocationIcon />}
                onClick={() => locationHandler(true)}
              >
                {t("device-menu.location-by-date")}
              </Nav.Item>
            </Nav.Menu>
          )}
          <Nav.Menu icon={<DeviceIcon />} title={t("device-menu.system")}>
            <Nav.Menu
              icon={<DeviceIcon />}
              title={t("device-menu.group-three")}
            >
              <Nav.Item icon={<DeviceIcon />}>
                {t("device-menu.group3-item-one")}
              </Nav.Item>
              <Nav.Item icon={<DeviceIcon />}>
                {t("device-menu.group3-item-two")}
              </Nav.Item>
            </Nav.Menu>
            {deviceSelected.type !== dev.types.android && deviceSelected.type !== dev.types.linux && (
              <Nav.Item
                icon={<Icon as={CiFloppyDisk} />}
                onClick={() => installedSoftwareHandler(true)}
              >
                {t("device-menu.software-installed")}
              </Nav.Item>
            )}
            {deviceSelected.type === dev.types.android && (
              <Nav.Item
                icon={<Icon as={CiFloppyDisk} />}
                onClick={() => installedAppsHandler(true)}
              >
                {t("device-menu.installed-applications")}
              </Nav.Item>
            )}
            <Nav.Item
              icon={<Icon as={GiGears} />}
              onClick={() => systemProcessesHandler(true)}
            >
              {t("device-menu.system-processes")}
            </Nav.Item>
            <Nav.Item icon={<GearIcon />}>
              {t("device-menu.system-services")}
            </Nav.Item>
            <Nav.Item icon={<Icon as={MdOutlineMonitorHeart} />}>
              {t("device-menu.system-health")}
            </Nav.Item>
            <Nav.Item icon={<UserInfoIcon />}>
              {t("device-menu.user-information")}
            </Nav.Item>
          </Nav.Menu>
          {deviceSelected.type !== dev.types.android && (
            <Nav.Menu
              icon={<Icon as={MdOutlineManageHistory} />}
              title={t("device-menu.management")}
            >
              <Nav.Item icon={<Icon as={BiScreenshot} />}>
                {t("device-menu.computer-screenshot")}
              </Nav.Item>
              <Nav.Item icon={<LockIcon />}>
                {t("device-menu.lock-workstation")}
              </Nav.Item>
              <Nav.Item icon={<OffIcon />}>
                {t("device-menu.log-off-user")}
              </Nav.Item>
              <Nav.Item icon={<ReloadIcon />}>
                {t("device-menu.reboot-workstation")}
              </Nav.Item>
              <Nav.Item icon={<PowerOffIcon />}>
                {t("device-menu.power-off-workstation")}
              </Nav.Item>
            </Nav.Menu>
          )}
          {deviceSelected.type !== dev.types.android && (
            <Nav.Menu
              icon={<FileIcon />}
              title={t("device-menu.file-transfer")}
            >
              <Nav.Item
                icon={<FileUploadIcon />}
                onClick={() => fileUploadHandler(true)}
              >
                {t("device-menu.upload-file")}
              </Nav.Item>
              <Nav.Item icon={<FileDownloadIcon />}>
                {t("device-menu.download-file")}
              </Nav.Item>
            </Nav.Menu>
          )}
          <Nav.Menu
            icon={<Icon as={RxUpdate} />}
            title={t("device-menu.agent-control")}
          >
            <Nav.Item icon={<InfoOutlineIcon />}>
              {t("device-menu.agent-info")}
            </Nav.Item>
            <Nav.Item icon={<Icon as={VscDebugRestart} />}>
              {t("device-menu.restart-agent")}
            </Nav.Item>
            <Nav.Item icon={<UserChangeIcon />}>
              {t("device-menu.update-agent")}
            </Nav.Item>
          </Nav.Menu>
          <Nav.Item icon={<TrashIcon />} onClick={deleteHandler}>
            {t("device-menu.delete-from-list")}
          </Nav.Item>
        </Nav>
      )}
      <Suspense>
        <RemoveDevice open={openModal} onClose={removeDeviceHandler} />
      </Suspense>
      {openInstalledSoftware && (
        <Suspense>
          <DeviceInstalledSoftware
            open={openInstalledSoftware}
            setOpen={installedSoftwareHandler}
          />
        </Suspense>
      )}
      {openSystemProcesses && (
        <Suspense>
          <DeviceSystemProcesses
            open={openSystemProcesses}
            setOpen={systemProcessesHandler}
          />
        </Suspense>
      )}
      {openLocation && (
        <Suspense>
          <LocationByDate open={openLocation} setOpen={locationHandler} />
        </Suspense>
      )}
      {openLiveLocation && (
        <Suspense>
          <LiveLocation open={openLiveLocation} setOpen={liveLocationHandler} />
        </Suspense>
      )}
      {openFileUpload && (
        <Suspense>
          <FileUploadHandler
            open={openFileUpload}
            setOpen={fileUploadHandler}
          />
        </Suspense>
      )}
      {openShell && (
        <Suspense>
          <RemoteShell open={openShell} setOpen={shellHandler} />
        </Suspense>
      )}
      {openShell2 && (
        <Suspense>
          <RemoteShell2 open={openShell2} setOpen={shellHandler2} />
        </Suspense>
      )}
      {openTerminal && (
        <Suspense>
          <Terminal open={openTerminal} setOpen={terminalHandler} />
        </Suspense>
      )}
      {openInstalledApps && (
        <Suspense>
          <DeviceInstalledApplications
            open={openInstalledApps}
            setOpen={installedAppsHandler}
          />
        </Suspense>
      )}
      {openLinuxApps && (
        <Suspense>
          <LinuxAppsSpotlight open={openLinuxApps} setOpen={linuxAppsHandler} />
        </Suspense>
      )}
      {openLinuxProcesses && (
        <Suspense>
          <LinuxProcessesList
            open={openLinuxProcesses}
            setOpen={linuxProcessesHandler}
          />
        </Suspense>
      )}
      {openLinuxHealth && (
        <Suspense>
          <LinuxHealthMonitor
            open={openLinuxHealth}
            setOpen={linuxHealthHandler}
          />
        </Suspense>
      )}
      {openRemoteDesktop && (
        <Suspense>
          <DeviceRemoteDesktop
            open={openRemoteDesktop}
            setOpen={remoteDesktopHandler}
          />
        </Suspense>
      )}
    </>
  );
};

export default DeviceMenu;
